import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { buildHubConnection } from '../lib/signalr';
import { notificationKeys } from '../api/notifications';
import { taskKeys } from '../api/tasks';
import type { NotificationDto, TaskDto } from '../types/api';

const KIND_LABEL: Record<string, string> = {
  EscalationT3:     'Task escalation — due in 3 days',
  EscalationT1:     'Task escalation — due tomorrow',
  EscalationOverdue: 'Task is now overdue',
  CheckInReminder:  'Check-in reminder',
  WeeklyPulse:      'Weekly pulse prompt',
};

export function useSignalR() {
  const qc = useQueryClient();
  const connectionRef = useRef(buildHubConnection());

  useEffect(() => {
    const conn = connectionRef.current;

    conn.on('notification.received', (notification: NotificationDto) => {
      // Refresh notification inbox so badge count and list update immediately.
      qc.invalidateQueries({ queryKey: notificationKeys.all() });
      const label = KIND_LABEL[notification.kind] ?? 'New notification';
      toast(label, { duration: 5000 });
    });

    conn.on('task.updated', (task: TaskDto) => {
      // Refresh the specific task detail and any list that might include it.
      qc.invalidateQueries({ queryKey: taskKeys.detail(task.id) });
      qc.invalidateQueries({ queryKey: taskKeys.all() });
    });

    conn.start().catch((err) => {
      // Non-fatal — app works without real-time; user just needs to refresh manually.
      console.warn('[SignalR] Connection failed, falling back to manual refresh:', err);
    });

    return () => {
      conn.stop();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
